import type { MedplumClient } from '@medplum/core';
import type { Appointment, Encounter, QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  AI_NOTE_CHECK_RESULTS_EXT,
  DETAILED_STATUS_EXT,
  PROVIDER_SIGNATURE_EXT_URL,
} from '@unify/fhir-model/constants';
import {
  applyAttendanceDerivedStatus,
  applyClinicianCancelToAppointment,
  appointmentNoteStatusLocksFulfillment,
  deriveEncounterOutcomeFromAppointment,
  deriveEncounterOutcomeFromAttendance,
  deriveAppointmentStatusFromAttendance,
  getParticipantAttendanceStatus,
  getPatientParticipants,
  isClientAppointmentParticipant,
  isResolvedClientAttendance,
  isSignAppointmentNoteResult,
  normalizeParticipantAttendanceCode,
} from '@unify/fhir-model/resources/appointment';
import { setReasonCancelled } from '@unify/fhir-model/resources/encounter';
import {
  getDetailedStatus,
  getQRTemplateCategory,
} from '@unify/fhir-model/resources/questionnaire-response';
import { someAppointmentNote } from '@/features/calendar/signedNoteAppointmentLock';
import {
  saveScheduledAppointmentAttendance,
  sealScheduledAppointmentNote,
  signScheduledAppointmentNote,
} from '@/lib/scheduleAppointmentService';
import { getAiNoteCheckResultsFromQR } from '../fhirNoteMapping';
import { UserFacingError } from '@/lib/userFacingError';

/** Medplum caches GET responses for 60s. Bot and transaction writes do not refresh that entry. */
const FRESH_READ = { cache: 'no-cache' } as const;

function invalidateResource(
  medplum: MedplumClient,
  resourceType: 'Appointment' | 'QuestionnaireResponse',
  id: string | undefined,
): void {
  if (!id) return;
  if (typeof medplum.invalidateUrl !== 'function' || typeof medplum.fhirUrl !== 'function') return;
  medplum.invalidateUrl(medplum.fhirUrl(resourceType, id));
}

const SIGNED_NOTE_LOCK_MESSAGE =
  'Cannot change appointment status after the linked clinical note is signed.';

class EncounterVersionConflict extends Error {
  constructor() {
    super('Encounter version conflict');
  }
}

function isPreconditionFailed(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return /412|precondition|If-Match|version/i.test(message) || (err as { status?: number }).status === 412;
}

function ifMatch(versionId: string | undefined): { headers: { 'If-Match': string } } | undefined {
  return versionId ? { headers: { 'If-Match': `W/"${versionId}"` } } : undefined;
}

export function isSignedNoteLockError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return message.includes(SIGNED_NOTE_LOCK_MESSAGE);
}

export async function appointmentNoteLocksFulfillment(
  medplum: MedplumClient,
  appointmentId: string,
): Promise<boolean> {
  return someAppointmentNote(medplum, appointmentId, (qr) => {
    const category = getQRTemplateCategory(qr);
    if (category != null && category !== 'appointments') return false;
    return appointmentNoteStatusLocksFulfillment(getDetailedStatus(qr));
  }, FRESH_READ);
}

/** Clinician-cancel attendance plus derived Appointment.status. Does not write. */
export function clinicianCancelWithDerivedStatus(appointment: Appointment): Appointment {
  return applyAttendanceDerivedStatus(applyClinicianCancelToAppointment(appointment));
}

export interface WriteAppointmentAttendanceArgs {
  appointmentId: string;
  /** Kept for callers that already hold the Appointment. The write itself is server-side. */
  appointment?: Appointment;
  attendance?: Array<{ participantReference: string; attendanceStatus: string }>;
  clinicianCancel?: boolean;
  /**
   * A user-initiated pick (session sidebar, note-metadata sync, note form; not the editor hook):
   * stamps attendance-source `manual`, but the bot only does so when an entry is resolved.
   */
  markManualSource?: boolean;
}

/**
 * Every attendance and status save goes through appointment-tools `save-attendance`.
 * The bot can read clinical notes the caller cannot, so an empty browser note search
 * cannot skip the signed-note lock.
 */
export async function writeAppointmentAttendance(
  medplum: MedplumClient,
  args: WriteAppointmentAttendanceArgs,
): Promise<Appointment> {
  const saved = await saveScheduledAppointmentAttendance(medplum, args.appointmentId, {
    attendance: args.attendance,
    clinicianCancel: args.clinicianCancel,
    attendanceSource: args.markManualSource ? 'manual' : undefined,
  });
  invalidateResource(medplum, 'Appointment', args.appointmentId);
  return saved;
}

type EncounterOutcome = {
  status: 'finished' | 'cancelled';
  reasonCancelled?: 'noshow' | 'client-cancelled' | 'client-late-cancelled' | 'clinician-cancelled';
};

/** Returns true when it wrote; false when the Encounter already carried the outcome. */
async function writeEncounter(
  medplum: MedplumClient,
  encounter: Encounter,
  next: EncounterOutcome,
): Promise<boolean> {
  const patched: Encounter = { ...encounter, status: next.status };
  setReasonCancelled(patched, next.status === 'cancelled' ? next.reasonCancelled : undefined);
  if (
    encounter.status === patched.status
    && JSON.stringify(encounter.extension) === JSON.stringify(patched.extension)
  ) {
    return false;
  }
  try {
    await medplum.updateResource(patched, ifMatch(encounter.meta?.versionId));
  } catch (err) {
    if (isPreconditionFailed(err)) throw new EncounterVersionConflict();
    throw err;
  }
  return true;
}

function encounterOutcomeFor(
  appointment: Appointment,
  outcome: NonNullable<ReturnType<typeof deriveAppointmentStatusFromAttendance>>,
  encounter: Encounter,
): EncounterOutcome | undefined {
  const patients = getPatientParticipants(appointment);
  if (patients.length > 1) {
    const participant = patients.find((p) => p.actor?.reference === encounter.subject?.reference);
    return participant
      ? deriveEncounterOutcomeFromAttendance(getParticipantAttendanceStatus(participant))
      : undefined;
  }
  return deriveEncounterOutcomeFromAppointment(outcome);
}

const SETTLE_ATTEMPTS = 4;

/**
 * The Appointment version check and the Encounter PUT are separate requests. A write that
 * passed the check can land after a newer outcome (signing) that skipped the Encounter because
 * it already matched. Re-derive from the latest Appointment and write until it holds still.
 * An Encounter 412 (unrelated metadata update) is retried from fresh reads of both resources.
 */
async function settleEncounterToLatestAppointment(
  medplum: MedplumClient,
  encounterId: string,
  appointmentId: string,
): Promise<void> {
  for (let attempt = 0; attempt < SETTLE_ATTEMPTS; attempt++) {
    const appointment = await medplum.readResource('Appointment', appointmentId, FRESH_READ);
    const outcome = deriveAppointmentStatusFromAttendance(appointment);
    if (!outcome) return;
    const encounter = await medplum.readResource('Encounter', encounterId, FRESH_READ);
    const next = encounterOutcomeFor(appointment, outcome, encounter);
    if (!next) return;
    try {
      await writeEncounter(medplum, encounter, next);
    } catch (err) {
      if (err instanceof EncounterVersionConflict) continue;
      throw err;
    }
    const after = await medplum.readResource('Appointment', appointmentId, FRESH_READ);
    if (after.meta?.versionId === appointment.meta?.versionId) return;
  }
  throw new EncounterVersionConflict();
}

/**
 * Post-write cascade for callers whose Appointment write already succeeded: a cascade failure is
 * logged, not thrown, so it cannot turn a saved Appointment into a failed save.
 */
export async function cascadeAppointmentEncountersAfterWrite(
  medplum: MedplumClient,
  appointment: Appointment,
): Promise<void> {
  try {
    await cascadeAppointmentEncounters(medplum, appointment);
  } catch (err) {
    console.warn('[attendanceFulfillment] Encounter cascade failed after Appointment write:', err);
  }
}

export async function cascadeAppointmentEncounters(
  medplum: MedplumClient,
  appointment: Appointment,
): Promise<void> {
  try {
    await cascadeAppointmentEncountersOnce(medplum, appointment);
  } catch (err) {
    if (!(err instanceof EncounterVersionConflict) || !appointment.id) throw err;
    const fresh = await medplum.readResource('Appointment', appointment.id, FRESH_READ);
    await cascadeAppointmentEncountersOnce(medplum, fresh);
  }
}

async function cascadeAppointmentEncountersOnce(
  medplum: MedplumClient,
  appointment: Appointment,
): Promise<void> {
  if (!appointment.id) return;
  const outcome = deriveAppointmentStatusFromAttendance(appointment);
  if (!outcome) return;

  for await (const encounters of medplum.searchResourcePages('Encounter', {
    appointment: `Appointment/${appointment.id}`,
    _count: '50',
  }, FRESH_READ)) {
    for (const encounter of encounters) {
      if (encounter.resourceType !== 'Encounter') continue;
      const next = encounterOutcomeFor(appointment, outcome, encounter);
      if (!next) continue;
      if (appointment.meta?.versionId) {
        const current = await medplum.readResource('Appointment', appointment.id, FRESH_READ);
        if (current.meta?.versionId !== appointment.meta.versionId) {
          throw new EncounterVersionConflict();
        }
      }
      const wrote = await writeEncounter(medplum, encounter, next);
      if (!wrote || !appointment.meta?.versionId || !encounter.id) continue;
      const after = await medplum.readResource('Appointment', appointment.id, FRESH_READ);
      if (after.meta?.versionId !== appointment.meta.versionId) {
        await settleEncounterToLatestAppointment(medplum, encounter.id, appointment.id);
      }
    }
  }
}

/**
 * Copy note-form participant attendance onto the Appointment through save-attendance.
 * Returns the same appointment when nothing on the note differs from the Appointment.
 */
function noteAttendanceEntries(
  appointment: Appointment,
  byPatientId: Record<string, string | undefined>,
): Array<{ participantReference: string; attendanceStatus: string }> {
  return (appointment.participant ?? []).flatMap((participant) => {
    const ref = participant.actor?.reference?.trim();
    if (!ref || !isClientAppointmentParticipant(participant)) return [];
    const patientId = ref.slice(ref.indexOf('/') + 1);
    const status = byPatientId[patientId]?.trim();
    if (!status) return [];
    return [{ participantReference: ref, attendanceStatus: status }];
  });
}

function attendanceDiffers(
  appointment: Appointment,
  entries: Array<{ participantReference: string; attendanceStatus: string }>,
): boolean {
  return entries.some((entry) => {
    const participant = appointment.participant?.find(
      (p) => p.actor?.reference === entry.participantReference,
    );
    const current = participant ? getParticipantAttendanceStatus(participant) : undefined;
    return normalizeParticipantAttendanceCode(current)
      !== normalizeParticipantAttendanceCode(entry.attendanceStatus);
  });
}

export async function persistNoteFormAttendance(
  medplum: MedplumClient,
  appointment: Appointment,
  byPatientId: Record<string, string | undefined>,
): Promise<Appointment> {
  if (!appointment.id) return appointment;
  const fresh = await medplum.readResource('Appointment', appointment.id, FRESH_READ);
  const attendance = noteAttendanceEntries(fresh, byPatientId);
  if (attendance.length === 0) return fresh;
  if (!attendanceDiffers(fresh, attendance)) return fresh;
  const saved = await writeAppointmentAttendance(medplum, {
    appointmentId: appointment.id,
    appointment: fresh,
    attendance,
    // Only a resolved pick that differs from the Appointment is the provider's own; the hydrated
    // `scheduled` default is still written (unchanged) but never marks the source manual.
    markManualSource: attendance.some(
      (entry) => isResolvedClientAttendance(entry.attendanceStatus) && attendanceDiffers(fresh, [entry]),
    ),
  });
  if (
    saved.status !== fresh.status
    || JSON.stringify(saved.participant) !== JSON.stringify(fresh.participant)
  ) {
    await cascadeAppointmentEncountersAfterWrite(medplum, saved);
  }
  return saved;
}

const NOTE_CHANGED_WHILE_SIGNING =
  'The note changed while signing. Review the latest note and sign again.';

function clinicalItems(note: QuestionnaireResponse): string {
  return JSON.stringify(note.item ?? []);
}

/**
 * Fields the signer did not review. Signature, detailed status, and acknowledgments
 * are applied by signing; everything else must still match the reviewed note.
 */
function noteOutsideSignature(note: QuestionnaireResponse): string {
  const checks = getAiNoteCheckResultsFromQR(note);
  const extension = (note.extension ?? [])
    .filter((ext) => ext.url !== PROVIDER_SIGNATURE_EXT_URL && ext.url !== DETAILED_STATUS_EXT)
    .map((ext) => {
      if (ext.url !== AI_NOTE_CHECK_RESULTS_EXT || !checks) return ext;
      return { ...ext, valueString: JSON.stringify({ ...checks, acknowledgments: undefined }) };
    });
  return JSON.stringify({
    item: note.item ?? [],
    extension,
    author: note.author,
    subject: note.subject,
    encounter: note.encounter,
    questionnaire: note.questionnaire,
    basedOn: note.basedOn,
    partOf: note.partOf,
    meta: note.meta
      ? { tag: note.meta.tag, profile: note.meta.profile, security: note.meta.security }
      : undefined,
  });
}

function signAttendanceEntries(
  byPatientId: Record<string, string | undefined> | undefined,
): Array<{ participantReference: string; attendanceStatus: string }> {
  return Object.entries(byPatientId ?? {}).flatMap(([patientId, status]) => {
    const trimmed = status?.trim();
    return trimmed ? [{ participantReference: `Patient/${patientId}`, attendanceStatus: trimmed }] : [];
  });
}

/**
 * Sign the note and seal the Appointment through the `sign-appointment-note` bot.
 * The bot checks the signature as the signer; the admin `appointment-tools`
 * `sign-and-seal-appointment` action persists it and locks the Appointment; the lock check runs as
 * admin over all linked notes. This side only guards that a retry signs what the clinician reviewed.
 * When the signature commits but the seal fails, the seal alone is retried (with backoff, never
 * re-signing); if it still fails a `SealPendingError` carries the persisted signed note.
 */
export async function commitSignedNoteAndSealAppointment(
  medplum: MedplumClient,
  signedNote: QuestionnaireResponse,
  appointmentId: string | undefined,
  byPatientId?: Record<string, string | undefined>,
  options: {
    sealRetryDelaysMs?: number[];
    /** Revise Note: the stored note is already signed and is being replaced by a new signature. */
    revision?: boolean;
  } = {},
): Promise<QuestionnaireResponse> {
  if (!appointmentId || !signedNote.id) {
    return medplum.updateResource(signedNote);
  }
  let note = signedNote;
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) {
      const freshNote = await medplum.readResource('QuestionnaireResponse', signedNote.id, FRESH_READ);
      if (
        clinicalItems(freshNote) !== clinicalItems(signedNote)
        || (
          freshNote.meta?.versionId !== signedNote.meta?.versionId
          && noteOutsideSignature(freshNote) !== noteOutsideSignature(signedNote)
        )
      ) {
        throw new UserFacingError(NOTE_CHANGED_WHILE_SIGNING);
      }
      // Appointment-only conflict: keep the reviewed note, including acknowledgments
      // and removed requested-edit flags. A newer note version is adopted only when
      // every field outside the signature still matches what was reviewed.
      note = freshNote.meta?.versionId === signedNote.meta?.versionId
        ? signedNote
        : { ...signedNote, meta: { ...signedNote.meta, versionId: freshNote.meta?.versionId } };
    }
    const attendance = signAttendanceEntries(byPatientId);
    let result: unknown;
    try {
      result = await signScheduledAppointmentNote(medplum, appointmentId, note, attendance, {
        revision: options.revision,
      });
    } catch (err) {
      if (err instanceof Error && err.message === NOTE_CHANGED_WHILE_SIGNING) throw err;
      if (!isPreconditionFailed(err)) throw err;
      if (attempt > 0) throw new UserFacingError('Could not sign the note because the appointment changed.');
      continue;
    }
    invalidateResource(medplum, 'QuestionnaireResponse', note.id);
    invalidateResource(medplum, 'Appointment', appointmentId);
    if (isSignAppointmentNoteResult(result) && !result.sealed) {
      // The signature committed; only the Appointment seal failed. Never re-sign: show the persisted
      // signed note and retry the seal alone.
      console.warn(`Appointment/${appointmentId} seal pending after signing:`, result.sealError);
      // The retry compares only the id, subject and answers, which the signed body carries, so when the
      // fresh read is unavailable the body stamped with the signed version is used for the retry only.
      // A fresh note whose answers differ (another session corrected it since) must not become the
      // "as signed" note the retry compares against, so only a same-answers note is used.
      const fresh = await readFreshSignedNote(medplum, note, result.noteVersionId);
      const persisted = fresh && clinicalContent(fresh) === clinicalContent(note)
        ? fresh
        : stampedAtSignedVersion(note, result.noteVersionId);
      const sealedNote = await retrySealWithBackoff(medplum, appointmentId, persisted, attendance, options.sealRetryDelaysMs);
      if (sealedNote) return sealedNote;
      throw new SealPendingError(persisted, appointmentId, attendance);
    }
    if (isSignAppointmentNoteResult(result)) {
      // Sealed: return the note the server persisted (server-built signature, fresh version), never the
      // client body. If the saved note cannot be read back, ask for a reload rather than show the body.
      const persisted = await readFreshSignedNote(medplum, note, result.noteVersionId);
      if (persisted) return persisted;
      throw new UserFacingError(SIGNED_AND_SEALED_RELOAD_MESSAGE);
    }
    return note;
  }
  return note;
}

/** Text shown when the note is signed but the Appointment seal has not landed yet. */
export const SEAL_PENDING_MESSAGE = 'Note signed; appointment update pending.';

const DEFAULT_SEAL_RETRY_DELAYS_MS = [1000, 3000];

/** The note is signed but its Appointment seal is still pending; carries the persisted signed note. */
export class SealPendingError extends UserFacingError {
  constructor(
    readonly signedNote: QuestionnaireResponse,
    readonly appointmentId: string,
    readonly attendance: Array<{ participantReference: string; attendanceStatus: string }>,
  ) {
    super(SEAL_PENDING_MESSAGE);
    this.name = 'SealPendingError';
  }
}

export function isSealPendingError(err: unknown): err is SealPendingError {
  return err instanceof SealPendingError;
}

/** Shown when the note is signed and sealed but the saved note could not be read back. */
export const SIGNED_AND_SEALED_RELOAD_MESSAGE = 'Note signed and sealed. Reload to view the saved signature.';

function stampedAtSignedVersion(note: QuestionnaireResponse, signedVersionId: string | undefined): QuestionnaireResponse {
  return signedVersionId ? { ...note, meta: { ...note.meta, versionId: signedVersionId } } : note;
}

/**
 * The note as the server saved it: a fresh read (provider policies grant read, not vread) accepted when
 * it is the signed version, or a later version that is still signed-or-later (the seal's compartment
 * restore writes a metadata-only version after the sign). Undefined when the read fails or the note is
 * not signed, so the client body is never presented as the saved note.
 */
async function readFreshSignedNote(
  medplum: MedplumClient,
  note: QuestionnaireResponse,
  signedVersionId: string | undefined,
): Promise<QuestionnaireResponse | undefined> {
  if (!signedVersionId) return undefined;
  try {
    const fresh = await medplum.readResource('QuestionnaireResponse', note.id!, FRESH_READ);
    if (
      fresh.meta?.versionId === signedVersionId
      || appointmentNoteStatusLocksFulfillment(getDetailedStatus(fresh))
    ) {
      return fresh;
    }
    return undefined;
  } catch (err) {
    console.warn(`QuestionnaireResponse/${note.id} read after signing failed:`, err);
    return undefined;
  }
}

async function retrySealWithBackoff(
  medplum: MedplumClient,
  appointmentId: string,
  signedNote: QuestionnaireResponse,
  attendance: Array<{ participantReference: string; attendanceStatus: string }>,
  delaysMs: number[] = DEFAULT_SEAL_RETRY_DELAYS_MS,
): Promise<QuestionnaireResponse | undefined> {
  for (const delayMs of delaysMs) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      return await retryAppointmentSeal(medplum, appointmentId, signedNote, attendance);
    } catch (err) {
      console.warn(`Appointment/${appointmentId} seal retry failed:`, err);
    }
  }
  return undefined;
}

/** Shown when a pending seal's note was edited after it was signed. */
export const NOTE_CHANGED_SINCE_SIGNED_MESSAGE =
  'The note changed since it was signed, so the appointment was not updated. Reopen the note and sign it again.';
/** Shown when a pending seal's note is no longer signed (e.g. returned for edits). */
export const NOTE_NO_LONGER_SIGNED_MESSAGE = 'The note is no longer signed, so the appointment was not updated.';

/** True for a seal-retry refusal that another retry of the same pending seal cannot fix. */
export function isSealRetryRefusal(err: unknown): boolean {
  return err instanceof UserFacingError
    && (err.message === NOTE_CHANGED_SINCE_SIGNED_MESSAGE || err.message === NOTE_NO_LONGER_SIGNED_MESSAGE);
}

/**
 * The note's clinical content for a seal retry: its subject and its answers (`item`, which also
 * carries the note-form attendance). `meta` (the seal's compartment restore, versions), `extension`
 * (workflow status, provider and supervisor signatures), `status` and `authored` are not compared,
 * so a metadata-only restore or a supervisor co-sign does not block the retry, while a return for
 * edits that changed any answer does.
 */
function clinicalContent(note: QuestionnaireResponse): string {
  return JSON.stringify({ subject: note.subject?.reference ?? null, item: note.item ?? [] });
}

/**
 * Retry only the Appointment seal for a note that is already signed (never re-signs). `signedNote`
 * is the note as persisted when it was signed; the retry's `attendance` came from that signature.
 * The note is read fresh: it must still be signed-or-later and carry the same clinical content
 * (else the old attendance could overwrite a correction), and the seal binds to its current version.
 */
export async function retryAppointmentSeal(
  medplum: MedplumClient,
  appointmentId: string,
  signedNote: QuestionnaireResponse,
  attendance: Array<{ participantReference: string; attendanceStatus: string }>,
): Promise<QuestionnaireResponse> {
  const noteId = signedNote.id!;
  const fresh = await medplum.readResource('QuestionnaireResponse', noteId, FRESH_READ);
  if (!appointmentNoteStatusLocksFulfillment(getDetailedStatus(fresh))) {
    throw new UserFacingError(NOTE_NO_LONGER_SIGNED_MESSAGE);
  }
  if (clinicalContent(fresh) !== clinicalContent(signedNote)) {
    throw new UserFacingError(NOTE_CHANGED_SINCE_SIGNED_MESSAGE);
  }
  await sealScheduledAppointmentNote(medplum, appointmentId, noteId, fresh.meta?.versionId, attendance);
  invalidateResource(medplum, 'QuestionnaireResponse', noteId);
  invalidateResource(medplum, 'Appointment', appointmentId);
  return fresh;
}
